import { useEffect, useState } from 'react';
import { getImageUrl } from '../services/firebase';

/**
 * A displayable URL for an Algolia hit's `imageUrl`.
 *
 * The index stores whatever the ref carried, which may be a bare Storage path or
 * a tokenless `firebasestorage.googleapis.com` URL — neither renders directly —
 * so those are re-signed through `getImageUrl`. Already-signed absolute URLs are
 * used as-is. Shared by the reference browser's cards and rows and by the
 * curated-collection reference picker, so every surface resolves identically.
 */
export function useResolvedHitImage(imageUrl: string | undefined): string | null {
  const [resolved, setResolved] = useState<string | null>(null);

  useEffect(() => {
    if (!imageUrl) {
      setResolved(null);
      return;
    }
    const needsResolving =
      !imageUrl.startsWith('http') ||
      (imageUrl.includes('firebasestorage.googleapis.com') &&
        !imageUrl.includes('token='));
    if (!needsResolving) {
      setResolved(imageUrl);
      return;
    }
    // Extract Storage path from raw Firebase URL if needed
    let storagePath = imageUrl;
    if (imageUrl.includes('firebasestorage.googleapis.com')) {
      const match = imageUrl.match(/\/o\/(.+?)(\?|$)/);
      if (match) storagePath = decodeURIComponent(match[1]);
    }
    let cancelled = false;
    getImageUrl(storagePath)
      .then((url) => { if (!cancelled) setResolved(url); })
      .catch(() => { if (!cancelled) setResolved(null); });
    return () => { cancelled = true; };
  }, [imageUrl]);

  return resolved;
}
